/**
 * API Client with Interceptor Pattern
 *
 * Features:
 * ✓ Browser sends HttpOnly cookies automatically (credentials: 'include')
 * ✓ 3 retries with exponential backoff (1s, 2s, 4s delays) for failed requests
 * ✓ 3 retries on 401/403 auth failures, then logout after exhaustion
 * ✓ Error notifications shown to user after retry exhaustion
 * ✓ S3 calls are not handled as it has auto headers
 * ✓ Configurable via RETRY_CONFIG in constants/apiConfig.js
 */

import { retryWithBackoff } from './apiRetry';
import { getEnv } from '../config/env';
import { RETRY_CONFIG, DEFAULT_API_HEADERS } from '../constants/apiConfig';
import store from '../redux/store';
import { signOut, stopPeriodicRefresh } from '../redux/slices/authSlice';
import {
    addErrorNotification,
    addWarningNotification,
} from '../redux/slices/notificationSlice';
import {
    AUTH_ERROR_CODES,
    AUTH_NOTIFICATION_MESSAGES,
    AUTH_NOTIFICATION_DELAYS,
    AUTH_RESPONSE_HEADERS,
} from '../constants/authNotifications';
import logger from './logger';
import { replaceLocation } from './navigation';

const API_BASE_PATH = getEnv('VITE_BASE_URL');

let groupAccessHandlingStarted = false;

/**
 * Builds HTTP headers.
 * No longer injects Authorization header — browser sends cookies automatically.
 */
const buildHeaders = (extraHeaders = {}, contentType = 'application/json') => {
    const headers = {
        ...DEFAULT_API_HEADERS,
        ...extraHeaders,
    };

    if (contentType && !headers['Content-Type']) {
        headers['Content-Type'] = contentType;
    }

    // REMOVED: No more Authorization header injection
    // Tokens are sent as HttpOnly cookies by the browser automatically

    return new Headers(headers);
};

const isServerError = (status) => status >= 500 && status < 600;
const isAuthError = (status) => status === 401 || status === 403;

const isGroupAccessDenied = (error) =>
    error?.status === 403
    && error?.code
    === AUTH_ERROR_CODES.USER_NOT_ENTITLED;

const handleUnauthorized = async (status) => {
    logger.warn(`Authentication failed with status ${status} after ${RETRY_CONFIG.maxRetries} retries. Triggering logout.`);

    try {
        store.dispatch(signOut());
    } catch (e) {
        console.error(
            'Failed to dispatch signOut after auth failure:',
            e
        );
    }
};

const handleGroupAccessDenied = async () => {
    if (groupAccessHandlingStarted) {
        return;
    }

    groupAccessHandlingStarted = true;

    store.dispatch(
        addWarningNotification({
            message:
                AUTH_NOTIFICATION_MESSAGES.USER_NOT_ENTITLED,
            isInline: true,
        })
    );

    try {
        store.dispatch(stopPeriodicRefresh());
    } catch (error) {
        logger.error(
            'Failed to stop refresh after group denial:',
            error
        );
    }

    window.setTimeout(async () => {
        try {
            await store.dispatch(signOut()).unwrap();
        } catch (error) {
            logger.error(
                'Failed to sign out after group denial:',
                error
            );
            replaceLocation('/login');
        }
    }, AUTH_NOTIFICATION_DELAYS.LOGOUT_AFTER_WARNING_MS);
};

const parseErrorResponse = async (response) => {
    const headerCode =
        response.headers?.get(
            AUTH_RESPONSE_HEADERS.SUMO_ERROR
        )
        || null;

    try {
        const data = await response.json();

        const detail = (
            data?.detail
            && typeof data.detail === 'object'
        )
            ? data.detail
            : null;

        const code =
            headerCode
            || detail?.error
            || data?.error
            || detail?.code
            || data?.code
            || null;

        if (Array.isArray(data?.detail) && data.detail.length > 0) {
            const firstDetail = data.detail[0];
            const message = firstDetail?.ctx?.error || firstDetail?.msg;

            if (message) {
                const headerStatusCode = response.headers?.get('status') || response.status;
                const isStatusValid = headerStatusCode == response.status;
                
                return {
                    code,
                    message: message.replace(/^Value error,\s*/i, ''),
                    detail: data.detail,
                    status: response.status,
                    isStatusValid,
                };
            }
        }

        if (detail?.message) {
            return {
                code,
                message: detail.message,
                detail,
            };
        }

        if (
            Array.isArray(data?.messages)
            && data.messages.length > 0
        ) {
            return {
                code,
                message: data.messages[0],
                detail: null,
            };
        }

        if (data?.message) {
            return {
                code,
                message: data.message,
                detail: null,
            };
        }
        // Backend error shape seen from e.g. the create-job endpoint:
        // { "error": "Dataset name 'test' already exists. Please enter
        // different name.", "code": "DATASET_NAME_ALREADY_EXISTS" }.
        // None of the checks above catch this shape (data.error holds the
        // human-readable message here, not a code), so without this the
        // function fell straight through to the generic "HTTP 400: ..."
        // fallback and the real backend message was silently dropped.
        if (
            typeof data?.error === 'string'
            && data.error.trim()
        ) {
            return {
                code,
                message: data.error,
                detail: null,
            };
        }

        return {
            code,
            message:
                `HTTP ${response.status}: ${response.statusText}`,
            detail: null,
        };
    } catch {
        // Ignore JSON parse errors
    }

    return {
        code: headerCode,
        message:
            `HTTP ${response.status}: ${response.statusText}`,
        detail: null,
    };
};

export const apiFetch = async (pathOrUrl, options = {}) => {
    const {
        method = 'GET',
        headers = {},
        body,
        signal,
        retry = true,
        contentType = 'application/json',
        ...rest
    } = options;

    const url = pathOrUrl.startsWith('http') ? pathOrUrl : `${API_BASE_PATH}${pathOrUrl}`;

    const doRequest = async () => {
        const response = await fetch(url, {
            method,
            headers: buildHeaders(headers, contentType),
            body,
            signal,
            credentials: 'include',
            ...rest,
        });

        if (!response.ok) {
            const parsed = await parseErrorResponse(response);
            const error = new Error(parsed.message);

            error.status = response.status;
            error.code = parsed.code;
            error.detail = parsed.detail;

            if (
                response.status === 403
                && parsed.code
                === AUTH_ERROR_CODES.USER_NOT_ENTITLED
            ) {
                error.isRetryable = false;
            } else if (
                isServerError(response.status)
                || isAuthError(response.status)
            ) {
                error.isRetryable = !!retry;
            } else {
                error.isRetryable = false;
            }

            throw error;
        }

        if (response.status === 204) {
            return 'No Content';
        }

        return response.json();
    };

    try {
        if (retry) {
            return await retryWithBackoff(doRequest);
        }

        return await doRequest();
    } catch (error) {
        if (isGroupAccessDenied(error)) {
            await handleGroupAccessDenied();
            throw error;
        }

        if (retry && error?.isRetryable !== false) {
            logger.error(`API call to ${url} failed after ${RETRY_CONFIG.maxRetries} retries.`, error.message);

            if (isAuthError(error.status)) {
                handleUnauthorized(error.status);
            }

            store.dispatch(addErrorNotification({
                message: `API request failed: ${error.message || 'Please try again later'}`,
                isInline: false
            }));
            error.notificationDispatched = true;
        }
        throw error;
    }
};

export const callWithAuthRetry = async (makeRequest) => {
    const doRequest = async () => {
        const response = await makeRequest();
        const { status } = response;

        if (status && (isAuthError(status) || isServerError(status))) {
            let parsed = null;

            if (status === 403) {
                parsed = await parseErrorResponse(response);
            }

            const groupAccessDenied = (
                status === 403
                && parsed?.code
                === AUTH_ERROR_CODES.USER_NOT_ENTITLED
            );

            const error = new Error(
                groupAccessDenied
                    ? parsed.message
                    : `HTTP ${status}`
            );

            error.status = status;
            error.code = parsed?.code || null;
            error.detail = parsed?.detail || null;
            error.isRetryable = !groupAccessDenied;

            throw error;
        }

        return response;
    };

    try {
        return await retryWithBackoff(doRequest);
    } catch (error) {
        if (isGroupAccessDenied(error)) {
            await handleGroupAccessDenied();
            throw error;
        }

        const status = error?.status;

        if (status && (isAuthError(status) || isServerError(status))) {
            if (isAuthError(status)) {
                handleUnauthorized(status);
            }

            logger.error(`API call failed after retries with status ${status}.`, error.message);

            store.dispatch(addErrorNotification({
                message: `${isAuthError(status) ? 'Authentication' : 'Server'} error: ${error.message || 'Please try again later'}`,
                isInline: false
            }));
        }
        throw error;
    }
};